<template>
  <div class="sales page page-with-table p-3">
    <div class="card mb-3 p-3">
      <div class="">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h4 class="mb-0 mr-4">
            {{ $t('Batch Lot Up for Sale') }}
            <router-link v-if="sale" :to="{ name: 'sales.lots', params: { id: sale.uuid }}">
              {{ sale.sale_no }}
            </router-link>
          </h4>
          <div>
            <h5 class="mb-0 d-inline-block mr-2">{{ $t('Category') }}</h5>
            <b-form-group class="mb-0 d-inline-block">
              <b-form-select @change="onCategoryChange($event)" :options="activeCategories"/>
            </b-form-group>
          </div>
          <div>
            <h5 class="mb-0 d-inline-block mr-2 ml-4">{{ $t('Lot Ranges:') }}</h5>
            <b-form-group class="mb-0 d-inline-block">
              <b-form-input type="number" step="1" @change="onStartChange($event)" v-model="startRange" min="1"
                            :disabled="!currentCategory" style="width: 80px"/>
            </b-form-group>
            <span class="ml-2 mr-2">-</span>
            <b-form-group class="mb-0 d-inline-block">
              <b-form-input type="number" step="1" @change="onEndChange($event)" v-model="endRange" :min="startRange"
                            :disabled="!currentCategory" style="width: 80px"/>
            </b-form-group>

          </div>
        </div>
        <div class="d-flex flex-nowrap justify-content-between align-items-center">
          <b-form-checkbox
              v-model="selectUnselectAll"
              :value="true"
              @change="onSelectAll"
              :unchecked-value="false"
              class="ml-3"
          >
            {{ selectUnselectAll ? $t('Unselect All') : $t('Select All') }}
          </b-form-checkbox>
          <div class="d-flex align-items-center">
            <h5 class="mb-0 mr-2 ml-4 white-space-nowrap">{{ $t('Lot No:') }}</h5>
            <b-input-group class="d-flex flex-nowrap mb-0 mr-2" style="width: 240px">
              <b-form-input type="number" step="1" v-model="moveToPosition" :min="startRange" :max="endRange"
                            :disabled="!currentCategory" style="width: 80px"/>
              <b-input-group-addon>

                <submit-button :show-loader="isInProgress" class="ml-auto mr-2" variant="success" @click="moveTo"
                               :disabled="!this.currentCategory || !this.startRange || !this.endRange">
                  {{ $t('Move to Position') }}
                </submit-button>
              </b-input-group-addon>
            </b-input-group>
            <submit-button :show-loader="isInProgress" class="ml-auto" variant="primary" @click="onSaveClick">
              {{ $t('Save') }}
            </submit-button>
          </div>
        </div>
      </div>
    </div>

    <div class="content-wrapper">
      <spinner :show="loading"/>
      <div v-if="!loading" class="h-100 lot-list-wrapper">
        <div v-if="!lots.data.length" class="text-center py-5 lead">
          {{ $t('Please Select Category') }}
        </div>
        <draggable
            class="flex-container flex-container-style fixed-height p-2"
            tag="div"
            key="uuid"
            animate="150"
            :list="lots.data"
            @change="onDragChange"
        >
          <b-card v-for="(item, index) in lots.data" :key="index"
                  :class="{'lot-card': true, 'lot-card-checked': item.checked}"
                  no-body>
            <div class="card-header">

            </div>
            <batch-lot-up-image :item="item" @onGalleryClick="showImageGallery"/>
            <b-card-body>
              <h6 class="title">{{ item.inventory.short_description | striptags | truncate(50) }}</h6>
              <div class="lot-no">
                <span><strong>{{ $t('Lot No:') }}</strong></span>
                <span v-if="item.lot_no && !currentCategory.dataChanged">{{ item.lot_no }}</span>
                <span v-else-if="currentCategory && currentCategory.start && currentCategory.end">
                  {{ (lots.currentPage - 1) * perPage + currentCategory.start + index }}
                </span>
                <span v-else>{{ $t('Not Set') }}</span>
              </div>
            </b-card-body>
            <b-card-footer class="d-flex align-items-center justify-content-between">
              <b-button variant="link" class="mr-2" @click="onEditClick(item)" v-permission="'change_inventoryitem'">
                {{ $t('Edit') }}
              </b-button>
              <div @mouseup="selectItem($event, item, index)">
                <span v-if="lots.checked[item.uuid]">
                  <font-awesome-icon icon="check-square" size="2x" class="text-primary"/>
                </span>
                <span v-else>
                  <font-awesome-icon :icon="['far', 'square']" size="2x" class="text-primary"/>
                </span>
              </div>
            </b-card-footer>
          </b-card>
        </draggable>
        <div v-if="currentCategory && currentCategory.items.length > perPage"
             class="paginator-wrapper py-2 bg-white">
          <b-pagination
              v-model="currentPage"
              :total-rows="currentCategory.items.length"
              :per-page="perPage"
              @change="onPageChange"
              aria-controls="my-table"
              class="m-0"
          ></b-pagination>
        </div>
      </div>
    </div>
    <image-viewer ref="imageViewer"/>
  </div>
</template>

<script>
  import {createNamespacedHelpers} from "vuex";
  import Spinner from "../../../core/components/common/Spinner";
  import draggable from 'vuedraggable';
  import _ from 'lodash';
  import SalesService from "./../SalesService";
  import ImageViewer from "@/core/components/image-viewer/ImageViewer";
  import BatchLotUpImage from "@/modules/sales/batch-category-lot-up/BatchLotUpImage";
  import SortIcon from "@/core/components/sort-icon/SortIcon";
  import {
    WS_BATCH_LOT_UP_PROGRESS,
  } from "@/modules/constants";
  import SubmitButton from "@/core/components/common/SubmitButton";
  import {Formatter} from "@/core/services/formatter.service";
  import i18n from "../../../shared/i18n";
  import {SOURCE_BULK_CATEGORY_LOT_UP} from "../../constants";
  import {ON_RESERVATION_MODAL_SAVE} from "../../../plugins/event-bus/event-bus-names";

  const {mapActions, mapState} = createNamespacedHelpers('sales');
  const {mapActions: lotActions} = createNamespacedHelpers('lots');
  const {mapActions: mapActionsGlobal, mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "BatchCategoryLotUp",
    components: {SubmitButton, SortIcon, BatchLotUpImage, ImageViewer, Spinner, draggable},
    data() {
      return {
        itemsMap: [],
        currentCategory: null,
        startRange: null,
        endRange: null,
        lots: {
          currentPage: 1,
          data: [],
          checked: {}
        },
        moveToPosition: 0,
        currentPage: 1,
        perPage: 100,
        lastSelectedItemIndex: null,
        progress: null,
        selectUnselectAll: false,
      }
    },
    computed: {
      ...mapState({
        loading: state => state.batchLotUpData.loading,
        sale: state => state.batchLotUpData.sale,
        items: state => state.batchLotUpData.items,
      }),
      ...globalMapState({
        currentBranch: state => state.currentBranch,
      }),
      isInProgress() {
        return this.progress !== null
      },
      activeCategories() {
        const categories = [];
        this.itemsMap.forEach(cat => categories.push({value: cat.uuid, text: cat.name}));
        return categories;
      }
    },
    methods: {
      ...mapActionsGlobal(['addProgress', 'removeProgress', 'updateProgressValue']),
      ...mapActions(['initBatchLotUpData']),
      ...lotActions(['showReservationModal', 'getModalObject']),
      onDragChange() {
        this.itemsMap = this.itemsMap.map(cat => {
          if (cat.uuid === this.currentCategory.uuid) {
            cat.dataChanged = true;
            cat.items = [...this.lots.data];
          }
          return cat;
        });
      },
      onSelectAll(checked) {
        this.currentCategory.items.forEach(item => {
          item.checked = checked;
          this.$set(this.lots.checked, item.uuid, checked)
        })
      },
      onEditClick(item) {
        this.showReservationModal({
          sale: item.auction,
          source: SOURCE_BULK_CATEGORY_LOT_UP
        });
        this.getModalObject({uuid: item.uuid, source: SOURCE_BULK_CATEGORY_LOT_UP});
      },
      onCategoryChange(categoryUUID) {
        this.resetItemDataOnChange();
        if (!categoryUUID) {
          this.currentCategory = null;
          return;
        }
        this.currentPage = 1;
        for (let i = 0; i < this.itemsMap.length; i++) {
          if (this.itemsMap[i].uuid === categoryUUID) {
            this.currentCategory = this.itemsMap[i];
            this.startRange = this.currentCategory.start;
            this.endRange = this.currentCategory.end;
            this.lots.currentPage = this.currentPage;
            this.lots.data = this.currentCategory ? this.currentCategory.items.slice((this.currentPage - 1) * this.perPage, ((this.currentPage - 1) * this.perPage) + this.perPage) : [];
            return;
          }
        }
      },
      resetItemDataOnChange: function () {
        if (this.currentCategory) {
          this.selectAllItems(false, this.currentCategory);
          this.currentCategory.items.splice((this.lots.currentPage - 1) * this.perPage, this.perPage, ...this.lots.data);
          this.lastSelectedItemIndex = null;
          this.selectUnselectAll = false;
        }
      },
      onPageChange(currentPage) {
        this.resetItemDataOnChange();
        this.lots.currentPage = currentPage;
        this.lots.data = this.currentCategory ? this.currentCategory.items.slice((currentPage - 1) * this.perPage, ((currentPage - 1) * this.perPage) + this.perPage) : [];
      },
      checkRangeOverlap(start, end) {
        start = parseInt(start);
        end = parseInt(end)
        if (!start || !end) {
          return false
        }
        for (let i = 0; i < this.itemsMap.length; i++) {
          const item = this.itemsMap[i];
          if (item.uuid === this.currentCategory.uuid) {

          } else {
            if (item.start && item.end && ((start >= item.start && start <= item.end) || (end >= item.start && end <= item.end))) {
              this.$errorDialog(i18n.t('Lot number range you have indicated overlaps lot range of another category: {categoryName}', {categoryName: item.name}));
              return true;
            }
          }
        }
        return false;
      },
      onStartChange(start) {
        if (this.currentCategory.end) {
          if (this.currentCategory.end < start) {
            this.startRange = this.currentCategory.start
            this.$errorDialog(i18n.t('Starting lot number must be less than the end lot number.'));
            return;
          }
          if (this.currentCategory.end - start + 1 < this.currentCategory.items.length) {
            this.startRange = this.currentCategory.start
            this.$errorDialog(i18n.t('Number of lots in this category are more than the lot number range you have indicated. Please increase the lot number range so that there is at least {lotCount} positions between the start and end.', {lotCount: this.currentCategory.items.length}));
            return;
          }
        }

        if (this.checkRangeOverlap(start, this.currentCategory.end)) {
          this.startRange = this.currentCategory.start
          return;
        }

        this.currentCategory.dataChanged = !!(start && this.currentCategory.end);
        this.currentCategory.start = parseInt(start);
      },
      onEndChange(end) {

        if (end === '') {
          this.currentCategory.dataChanged = !!(end && this.currentCategory.start);
          this.currentCategory.end = end;
          return;
        }
        if (this.currentCategory.start) {
          if (this.currentCategory.start > end) {
            this.$errorDialog(i18n.t('Starting lot number must be less than the end lot number.'));
            this.endRange = this.currentCategory.end
            return;
          }
          if (end - this.currentCategory.start + 1 < this.currentCategory.items.length) {
            this.endRange = this.currentCategory.end
            this.$errorDialog(i18n.t('Number of lots in this category are more than the lot number range you have indicated. Please increase the lot number range so that there is at least {lotCount} positions between the start and end.', {lotCount: this.currentCategory.items.length}));
            return;
          }
        }

        if (this.checkRangeOverlap(this.currentCategory.start, end)) {
          this.endRange = this.currentCategory.end
          return;
        }

        this.currentCategory.dataChanged = !!(end && this.currentCategory.start);

        this.currentCategory.end = end;
      },
      selectAllItems(checked, outerData) {
        this.lots.checked = {};
        outerData.checked = checked;
        outerData.items.forEach((item) => {
          item.checked = checked;
        });
      },
      selectItem(event, item, index) {
        const checked = !item.checked;
        if (event.shiftKey && this.lastSelectedItemIndex !== null && this.lastSelectedItemIndex != index) {
          const end = Math.max(index, this.lastSelectedItemIndex);
          for (let start = Math.min(index, this.lastSelectedItemIndex); start <= end; start++) {
            let item1 = this.lots.data[start];
            if (start !== index) {
              item1.checked = checked;
              this.$set(this.lots.checked, item1.uuid, checked);
            }
          }
        }
        this.lots.checked[item.uuid] = checked;
        item.checked = checked

        this.lastSelectedItemIndex = index;
      },
      initDataByCategory(items) {
        this.itemsMap = [];
        items.forEach((item) => {
          let sourceArr = this.itemsMap;

          let mappedObj = sourceArr.find(i => i.uuid === item.inventory.category.uuid);
          if (!mappedObj) {
            sourceArr.push({
              name: Formatter.getByMasterLanguage(item.inventory.category.name),
              uuid: item.inventory.category.uuid,
              checked: false,
              orderDesc: null,
              start: null,
              end: null,
              startEdge: null,
              endEdge: null,
              items: []
            });

            mappedObj = sourceArr[sourceArr.length - 1];
          }
          if (item.lot_no) {
            const lotNumber = parseInt(item.lot_no);
            if (!mappedObj.start || mappedObj.start > lotNumber) {
              mappedObj.start = lotNumber;
            }
            if (!mappedObj.end || mappedObj.end < lotNumber) {
              mappedObj.end = lotNumber;
            }
          }
          mappedObj.items.push({...item, checked: false});
        });

        this.itemsMap.forEach(item => {
          const tmp = this.sale.category_lot_ranges[item.uuid] || (this.currentBranch.category_lot_ranges[item.uuid] || {})
          item.start = tmp.min || null;
          item.end = tmp.max || null;
        })
      },
      getImageSrc(images) {
        for (let img of images) {
          if (img.data && (img.data.thumb || img.data.original)) {
            return img.data.thumb ? img.data.thumb.url : img.data.original.url;
          }
        }
        return null;
      },
      showImageGallery(data) {
        this.$refs.imageViewer.show(data);
      },
      async addProgressIfNotExist() {
        if (!this.progress) {
          this.progress = await this.addProgress(
              this.$i18n.t("Batch lot up for {sale_no}", {sale_no: this.sale.sale_no})
          );
        }
        return true
      },
      removeProgressIfExist() {
        if (this.progress) {
          this.removeProgress(this.progress.uuid)
          this.progress = null;
        }
      },
      async onProgressNotificationReceive(data) {
        await this.addProgressIfNotExist()
        this.updateProgressValue({
          progressValue: parseInt(Math.round(data.successful / data.total * 100)),
          uuid: this.progress.uuid
        });

        if (data.is_finished) {
          // this.currentCategory = null;
          // this.startRange = null;
          // this.endRange = null;
          this.removeProgressIfExist();

          if (!data.errors) {
            // ##########################################
            // ##### KEEP THIS UNTIL THERE IS A FIX #####
            // ##########################################
            function sleep(ms) {
              return new Promise(resolve => setTimeout(resolve, ms));
            }

            await sleep(this.items.length * 10);
            // #########################################
            this.$toast('success', this.$i18n.t("Batch lot up has successfully finished"));
            this.itemsMap.forEach((category) => category.dataChanged = false)

            await this.initBatchLotUpData(this.$route.params.sale_no);
            this.initDataByCategory(_.cloneDeep(this.items));
          }
        }
        if (data.errors) {
          if (data.errors.non_field_errors) {
            this.$errorDialog(data.errors.non_field_errors[0])
          } else if (data.errors.errors) {
            const listItemsArr = [];
            data.errors.errors.forEach(err => listItemsArr.push(this.$createElement('li', {}, err)))
            this.$errorDialog(this.$createElement('ul', {class: "mb-0"}, listItemsArr), this.$i18n.t('Validation Error'));
          } else {
            this.$errorDialog(this.$i18n.t("Something went wrong while running batch lot up. Please, try again later."))
          }
        }
      },
      async onSaveClick() {
        let requestData = [];
        this.itemsMap.forEach((category) => {
          requestData.push(...category.items.map((item, index) => {
            const data = {uuid: item.uuid};
            if (!category.start || !category.end) {
              data.lot_no = item.lot_no;
            } else {
              data.lot_no = category.start + index;
            }
            return data;
          }))
        });
        let response = await SalesService.batchLotUp(this.sale.sale_no, requestData.filter(item => item.lot_no !== null && item.lot_no !== ''));
        await this.addProgressIfNotExist();
        if (response.success) {
          SalesService.updateCategoryLotRanges(this.sale.uuid, this.currentCategory.uuid, this.startRange, this.endRange)
          this.$toast('success', this.$i18n.t('Batch lot up has successfully started'))
        } else if (response.body.non_field_errors) {
          this.$errorDialog(response.body.non_field_errors[0])
        }
      },
      moveTo() {
        if (!this.moveToPosition || parseInt(this.moveToPosition) > this.endRange || parseInt(this.moveToPosition) < this.startRange) {
          this.$errorDialog(this.$i18n.t("New position must be in between start and end ranges."));
          return;
        }
        // snapshot lots.data
        this.currentCategory.items.splice((this.lots.currentPage - 1) * this.perPage, this.perPage, ...this.lots.data);

        // move selected items into original array
        const tempArray = [];
        let index = 0;
        while (index < this.currentCategory.items.length) {
          if (this.currentCategory.items[index].checked) {
            tempArray.push(...this.currentCategory.items.splice(index, 1));
          } else {
            index++;
          }
        }

        this.currentCategory.dataChanged = true;

        const nIndex = parseInt(this.moveToPosition) - this.currentCategory.start
        // const nIndex = ((this.currentPage - 1) * this.perPage + this.currentCategory.start - parseInt(this.moveToPosition)) * -1;
        this.currentCategory.items.splice(nIndex, 0, ...tempArray);
        this.selectAllItems(false, this.currentCategory);
        // Take new data into lots.data
        this.lots.data = this.currentCategory ? this.currentCategory.items.slice((this.currentPage - 1) * this.perPage, ((this.currentPage - 1) * this.perPage) + this.perPage) : [];
      },
      // dataChanged() {
      //   return Utils.isDifferent(JSON.parse(this.getState()), JSON.parse(this.oldState));
      // }
    },
    async beforeMount() {
      await this.initBatchLotUpData(this.$route.params.sale_no);
      this.initDataByCategory(_.cloneDeep(this.items));
    },
    mounted() {
      // listen on event
      this.$evBus.$on(ON_RESERVATION_MODAL_SAVE, async () => {
        await this.initBatchLotUpData(this.$route.params.sale_no);
        this.initDataByCategory(_.cloneDeep(this.items));
        this.onCategoryChange(this.currentCategory.uuid)
      })
      this.$ws.listen(`sales.${WS_BATCH_LOT_UP_PROGRESS}`, this.onProgressNotificationReceive);
    },
    beforeDestroy() {
      this.removeProgressIfExist();
      this.$ws.remove(`sales.${WS_BATCH_LOT_UP_PROGRESS}`);
    },
    beforeRouteLeave(to, from, next) {
      let changed = false;
      this.itemsMap.forEach((cat) => {
        if (cat.dataChanged) {
          changed = true
          return false;
        }
      })

      if (changed) {
        this.$confirm(this.$i18n.t('Are you sure you want discard current changes?')).then(async (success) => {
          if (success) next();
        });
      } else {
        next();
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "./../scss/variables";

  .lot-card {
    width: 200px;
  }

  .lot-list-wrapper {
    display: flex;
    flex-direction: column;

  }

  .paginator-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .flex-container {
    display: flex;
    flex-direction: row;
    gap: 1rem;
    flex-wrap: wrap;
    flex: 1;
    justify-content: center;
    align-content: stretch;
    align-items: flex-start;
    overflow: auto;

    .lot-card:hover {
      .card-header {
        display: flex;
      }
    }

    .lot-card-display-header {
      .card-header {
        display: flex;
      }
    }

    .lot-card-checked {
      box-shadow: 0 0 0 3px #03a9f4;
    }

    .card-header {
      display: none;
      background-color: transparent;
      justify-content: flex-end;
      position: absolute;
      z-index: 500;
      width: 100%;
      border: 0;
    }

    .lot-no {
      display: flex;
      justify-content: space-between;
    }
  }

  .title {
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 150px;
    white-space: nowrap;
  }
</style>

